import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ConflictCheckResponse,
  MdrRequestPassengers,
  MdrResponse,
  RestaurantBody,
  RestaurantDetail,
  RestaurantListResponse,
} from '../model/mdr.model';

@Injectable({
  providedIn: 'root'
})
export class MdrService {
  private api_prefix: string;

  constructor(private http: HttpClient) {
    this.api_prefix = environment.backEndUrl;
  }

  getMdrRequests(
    page: number = 1,
    page_size: number = 20,
    ships?: string[],
    passenger_name?: string,
    date_from?: string,
    date_to?: string,
    cabin?: string,
    sort_by?: string,
    sort_order?: string
  ): Observable<MdrResponse> {
    const url = this.api_prefix + '/mdr/requests';

    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('page_size', page_size.toString());
    if (ships?.length)  ships.forEach(s => params = params.append('ships', s));
    if (passenger_name) params = params.set('passenger_name', passenger_name);
    if (date_from)      params = params.set('date_from', date_from);
    if (date_to)        params = params.set('date_to', date_to);
    if (cabin)          params = params.set('cabin', cabin);
    if (sort_by)        params = params.set('sort_by', sort_by);
    if (sort_order)     params = params.set('sort_order', sort_order);

    return this.http.get<MdrResponse>(url, { params });
  }

  getMdrPassengers(mdr_request_id: string): Observable<MdrRequestPassengers> {
    const url = `${this.api_prefix}/mdr/requests/${mdr_request_id}/passengers`;
    return this.http.get<MdrRequestPassengers>(url);
  }

  acceptRequest(mdr_request_id: string): Observable<void> {
    return this.http.put<void>(`${this.api_prefix}/mdr/requests/${mdr_request_id}/accept`, { processed_time: this.browserLocalNow() });
  }

  rejectRequest(mdr_request_id: string): Observable<void> {
    return this.http.put<void>(`${this.api_prefix}/mdr/requests/${mdr_request_id}/reject`, { processed_time: this.browserLocalNow() });
  }

  /**
   * Current browser wall-clock time as `YYYY-MM-DD HH:mm:ss` (no timezone info).
   * The backend stores this string verbatim, so what the browser shows (e.g.
   * 12:05) is exactly what is persisted — independent of DB/server timezone.
   */
  private browserLocalNow(): string {
    const d = new Date();
    const p = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
           `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  }

  exportMdrRequests(
    ships?: string[],
    passenger_name?: string,
    date_from?: string,
    date_to?: string,
    cabin?: string,
    sort_by?: string,
    sort_order?: string
  ): Observable<HttpResponse<Blob>> {
    const url = this.api_prefix + '/mdr/requests/export';

    let params = new HttpParams();
    if (ships?.length)  ships.forEach(s => params = params.append('ships', s));
    if (passenger_name) params = params.set('passenger_name', passenger_name);
    if (date_from)      params = params.set('date_from', date_from);
    if (date_to)        params = params.set('date_to', date_to);
    if (cabin)          params = params.set('cabin', cabin);
    if (sort_by)        params = params.set('sort_by', sort_by);
    if (sort_order)     params = params.set('sort_order', sort_order);

    return this.http.get(url, { params, responseType: 'blob', observe: 'response' });
  }

  getMdrHistoryRequests(
    page: number = 1,
    page_size: number = 20,
    ships?: string[],
    passenger_name?: string,
    date_from?: string,
    date_to?: string,
    cabin?: string,
    sort_by?: string,
    sort_order?: string,
    status?: string,
    restaurant?: string,
    time_changed?: string
  ): Observable<MdrResponse> {
    const url = this.api_prefix + '/mdr/requests/history';

    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('page_size', page_size.toString());
    if (ships?.length)  ships.forEach(s => params = params.append('ships', s));
    if (passenger_name) params = params.set('passenger_name', passenger_name);
    if (date_from)      params = params.set('date_from', date_from);
    if (date_to)        params = params.set('date_to', date_to);
    if (cabin)          params = params.set('cabin', cabin);
    if (sort_by)        params = params.set('sort_by', sort_by);
    if (sort_order)     params = params.set('sort_order', sort_order);
    if (status)         params = params.set('status', status);
    if (restaurant)     params = params.set('restaurant', restaurant);
    if (time_changed)   params = params.set('time_changed', time_changed);

    return this.http.get<MdrResponse>(url, { params });
  }

  exportMdrHistoryRequests(
    ships?: string[],
    passenger_name?: string,
    date_from?: string,
    date_to?: string,
    cabin?: string,
    sort_by?: string,
    sort_order?: string,
    status?: string,
    restaurant?: string,
    time_changed?: string
  ): Observable<HttpResponse<Blob>> {
    const url = this.api_prefix + '/mdr/requests/history/export';

    let params = new HttpParams();
    if (ships?.length)  ships.forEach(s => params = params.append('ships', s));
    if (passenger_name) params = params.set('passenger_name', passenger_name);
    if (date_from)      params = params.set('date_from', date_from);
    if (date_to)        params = params.set('date_to', date_to);
    if (cabin)          params = params.set('cabin', cabin);
    if (sort_by)        params = params.set('sort_by', sort_by);
    if (sort_order)     params = params.set('sort_order', sort_order);
    if (status)         params = params.set('status', status);
    if (restaurant)     params = params.set('restaurant', restaurant);
    if (time_changed)   params = params.set('time_changed', time_changed);

    return this.http.get(url, { params, responseType: 'blob', observe: 'response' });
  }

  // --- Restaurants & shifts (FUN6 — Cap. 2.2.6) ----------------------------

  getRestaurants(
    page: number = 1,
    page_size: number = 20,
    ships?: string[],
    sort_by?: string,
    sort_order?: string
  ): Observable<RestaurantListResponse> {
    const url = this.api_prefix + '/mdr/restaurants';

    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('page_size', page_size.toString());
    if (ships?.length) ships.forEach(s => params = params.append('ships', s));
    if (sort_by)       params = params.set('sort_by', sort_by);
    if (sort_order)    params = params.set('sort_order', sort_order);

    return this.http.get<RestaurantListResponse>(url, { params });
  }

  getRestaurant(restaurant_id: number): Observable<RestaurantDetail> {
    return this.http.get<RestaurantDetail>(`${this.api_prefix}/mdr/restaurants/${restaurant_id}`);
  }

  createRestaurant(body: RestaurantBody): Observable<RestaurantDetail> {
    return this.http.post<RestaurantDetail>(`${this.api_prefix}/mdr/restaurants`, body);
  }

  updateRestaurant(restaurant_id: number, body: RestaurantBody): Observable<RestaurantDetail> {
    return this.http.put<RestaurantDetail>(`${this.api_prefix}/mdr/restaurants/${restaurant_id}`, body);
  }

  deleteRestaurant(restaurant_id: number): Observable<void> {
    return this.http.delete<void>(`${this.api_prefix}/mdr/restaurants/${restaurant_id}`);
  }

  checkRestaurantConflict(
    ship_id: string,
    restaurant_code: string,
    chosen_stage: string,
  ): Observable<ConflictCheckResponse> {
    return this.http.get<ConflictCheckResponse>(
      `${this.api_prefix}/mdr/restaurants/conflict/${ship_id}/${restaurant_code}/${chosen_stage}`,
    );
  }
}
